import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiEdit } from 'react-icons/fi'
import { DataTable } from '@/components/common/DataTable'
import type { ActionButton, ColumnConfig } from '@/components/common/DataTable'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useAuth } from '@/contexts/AuthContext'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { useToast } from '@/hooks/useToast'
import {
  cooldownService,
  type CooldownOperationTypeRow,
  type CooldownOperationTypeUpdatePayload,
} from '@/services/admin/cooldownService'

type EditFormState = {
  displayName: string
  defaultDurationSeconds: string
  description: string
  minDurationSeconds: string
  maxDurationSeconds: string
  adminOverrideAllowed: boolean
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

const toBool = (value: unknown): boolean =>
  value === true || value === 1 || value === '1'

const toOptionalNumber = (value: string): number | null => {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number.parseInt(trimmed, 10)
  return Number.isFinite(parsed) ? parsed : null
}

export function CooldownOperationTypesTab({ cardBg }: { cardBg: string }) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  const { checkPermission } = useAuth()
  const canConfigure = checkPermission('cooldown:config')
  const modal = useDisclosure()
  const [editing, setEditing] = useState<CooldownOperationTypeRow | null>(null)
  const [form, setForm] = useState<EditFormState>({
    displayName: '',
    defaultDurationSeconds: '',
    description: '',
    minDurationSeconds: '',
    maxDurationSeconds: '',
    adminOverrideAllowed: false,
  })

  const queryKey = ['cooldown', 'operationTypes', currentEnv]
  const operationTypesQuery = useQuery({
    queryKey,
    queryFn: () => cooldownService.getOperationTypes(currentEnv),
  })

  const updateMutation = useMutation({
    mutationFn: (payload: { operationType: string; update: CooldownOperationTypeUpdatePayload }) =>
      cooldownService.updateOperationType(currentEnv, payload.operationType, payload.update),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey })
      toast.success({
        title: t('systemConfig.cooldown.operationTypes.updated'),
      })
      modal.onClose()
      setEditing(null)
    },
    onError: (error) => {
      toast.error({
        title: t('systemConfig.cooldown.operationTypes.updateFailed'),
        description: error instanceof Error ? error.message : String(error),
      })
    },
  })

  const rows = operationTypesQuery.data?.dbTypes ?? []

  const columns = useMemo<ColumnConfig<CooldownOperationTypeRow>[]>(() => [
    {
      key: 'operation_type',
      title: t('systemConfig.cooldown.columns.operationType'),
      width: '18%',
      render: value => <Text fontFamily="mono" fontSize="xs">{String(value)}</Text>,
    },
    {
      key: 'display_name',
      title: t('systemConfig.cooldown.columns.displayName'),
      width: '18%',
    },
    {
      key: 'default_duration_seconds',
      title: t('systemConfig.cooldown.columns.duration'),
      width: '12%',
      align: 'right',
    },
    {
      key: 'storage_strategy',
      title: t('systemConfig.cooldown.columns.storage'),
      width: '10%',
      render: value => (
        <Badge colorScheme={value === 'd1' ? 'blue' : 'purple'}>{String(value)}</Badge>
      ),
    },
    {
      key: 'admin_override_allowed',
      title: t('systemConfig.cooldown.columns.override'),
      width: '10%',
      render: value => (
        <Badge colorScheme={toBool(value) ? 'green' : 'gray'}>
          {t(`systemConfig.cooldown.states.${toBool(value) ? 'allowed' : 'blocked'}`)}
        </Badge>
      ),
    },
    {
      key: 'updated_at',
      title: t('systemConfig.cooldown.columns.updatedAt'),
      width: '18%',
      render: value => formatDateTime(value),
    },
  ], [t])

  const openEditModal = (row: CooldownOperationTypeRow) => {
    setEditing(row)
    setForm({
      displayName: row.display_name ?? '',
      defaultDurationSeconds: String(row.default_duration_seconds ?? ''),
      description: row.description ?? '',
      minDurationSeconds: row.min_duration_seconds === null ? '' : String(row.min_duration_seconds),
      maxDurationSeconds: row.max_duration_seconds === null ? '' : String(row.max_duration_seconds),
      adminOverrideAllowed: toBool(row.admin_override_allowed),
    })
    modal.onOpen()
  }

  const actions = (row: CooldownOperationTypeRow): ActionButton[] => [
    {
      label: t('common.edit'),
      ariaLabel: t('common.edit'),
      icon: <FiEdit />,
      iconOnly: true,
      variant: 'ghost',
      size: 'sm',
      disabled: !canConfigure,
      tooltip: canConfigure
        ? t('common.edit')
        : t('systemConfig.cooldown.permissions.configRequired'),
      onClick: () => openEditModal(row),
    },
  ]

  const submitUpdate = () => {
    if (!editing) return
    const update: CooldownOperationTypeUpdatePayload = {
      displayName: form.displayName,
      defaultDurationSeconds: Number.parseInt(form.defaultDurationSeconds, 10),
      description: form.description.trim() ? form.description : null,
      minDurationSeconds: toOptionalNumber(form.minDurationSeconds),
      maxDurationSeconds: toOptionalNumber(form.maxDurationSeconds),
      adminOverrideAllowed: form.adminOverrideAllowed,
    }
    updateMutation.mutate({ operationType: editing.operation_type, update })
  }

  return (
    <VStack spacing={4} align="stretch">
      <Card bg={cardBg}>
        <CardHeader>
          <HStack justify="space-between" align="start">
            <Box>
              <Heading size="md">
                {t('systemConfig.cooldown.operationTypes.title')}
              </Heading>
              <Text color="gray.600" fontSize="sm">
                {t(`systemConfig.general.network${currentEnv === 'mainnet' ? 'Mainnet' : 'Testnet'}`)}
              </Text>
            </Box>
            <RefreshIconButton
              label={t('common.refresh')}
              onClick={() => operationTypesQuery.refetch()}
              isLoading={operationTypesQuery.isFetching}
            />
          </HStack>
        </CardHeader>
        <CardBody pt={0}>
          <DataTable<CooldownOperationTypeRow>
            data={rows}
            columns={columns}
            loading={operationTypesQuery.isLoading}
            actions={actions}
            actionsColumnWidth="72px"
            emptyMessage={t('systemConfig.cooldown.operationTypes.empty')}
          />
        </CardBody>
      </Card>

      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader>{editing?.operation_type}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>
                  {t('systemConfig.cooldown.columns.displayName')}
                </FormLabel>
                <Input
                  value={form.displayName}
                  onChange={event => setForm(prev => ({ ...prev, displayName: event.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>
                  {t('systemConfig.cooldown.columns.duration')}
                </FormLabel>
                <Input
                  type="number"
                  min={1}
                  value={form.defaultDurationSeconds}
                  onChange={event => setForm(prev => ({
                    ...prev,
                    defaultDurationSeconds: event.target.value,
                  }))}
                />
              </FormControl>
              <HStack w="100%">
                <FormControl>
                  <FormLabel>{t('systemConfig.cooldown.columns.minDuration')}</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    value={form.minDurationSeconds}
                    onChange={event => setForm(prev => ({
                      ...prev,
                      minDurationSeconds: event.target.value,
                    }))}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.cooldown.columns.maxDuration')}</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    value={form.maxDurationSeconds}
                    onChange={event => setForm(prev => ({
                      ...prev,
                      maxDurationSeconds: event.target.value,
                    }))}
                  />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>{t('systemConfig.cooldown.columns.description')}</FormLabel>
                <Textarea
                  value={form.description}
                  onChange={event => setForm(prev => ({ ...prev, description: event.target.value }))}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb={0}>
                  {t('systemConfig.cooldown.columns.override')}
                </FormLabel>
                <Switch
                  isChecked={form.adminOverrideAllowed}
                  onChange={event => setForm(prev => ({
                    ...prev,
                    adminOverrideAllowed: event.target.checked,
                  }))}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={modal.onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="admin"
                onClick={submitUpdate}
                isLoading={updateMutation.isPending}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}
